import { useState, useMemo, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Edit, Eye, Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useGetPagesQuery } from "@/features/api/pageApi";

const PagesList = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState("updatedAt");
  const [sortOrder, setSortOrder] = useState("desc");

  const navigate = useNavigate();

  // Build API params
  const queryArgs = useMemo(
    () => ({
      search: searchQuery.trim() || undefined,
      sortBy,
      sortOrder,
    }),
    [searchQuery, sortBy, sortOrder]
  );

  // Fetch pages
  const { data, isLoading, isFetching, isError, error } = useGetPagesQuery(
    queryArgs,
    {
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,
      refetchOnReconnect: true,
    }
  );

  // Normalize API data
  const pages = useMemo(
    () => (Array.isArray(data?.pages) ? data.pages : []),
    [data]
  );

  // Sort pages
  const sortedPages = useMemo(() => {
    return [...pages].sort((a, b) => {
      let aVal, bVal;

      switch (sortBy) {
        case "title":
          aVal = a.title || "";
          bVal = b.title || "";
          break;
        case "slug":
          aVal = a.slug || "";
          bVal = b.slug || "";
          break;
        case "status":
          aVal = a.status || "";
          bVal = b.status || "";
          break;
        case "updatedAt":
          aVal = new Date(a.updatedAt ?? 0).getTime();
          bVal = new Date(b.updatedAt ?? 0).getTime();
          break;
        default:
          aVal = new Date(a.updatedAt ?? 0).getTime();
 ack
          bVal = new Date(b.updatedAt ?? 0).getTime();
      }

      if (typeof aVal === "string" && typeof bVal === "string") {
        return sortOrder === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      return sortOrder === "asc" ? aVal - bVal : bVal - aVal;
    });
  }, [pages, sortBy, sortOrder]);

  // Toggle sorting
  const toggleSortOrder = useCallback(
    (field) => {
      setSortBy(field);
      setSortOrder((prev) => (prev === "asc" && field === sortBy ? "desc" : "asc"));
    },
    [sortBy]
  );

  // Preview handler
  const handlePreview = (e, page) => {
    e.stopPropagation();
    const url = page.status === "published" ? `/${page.slug}` : `/${page.slug}`;
    window.open(url, "_blank");
  };

  // Format date
  const formatDate = (date) => {
    if (!date) return "N/A";
    const d = new Date(date);
    return isNaN(d) ? "N/A" : d.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Error handling
  useEffect(() => {
    if (isError) {
      if (error?.status === 401) {
        toast.error("Session expired. Please log in again.");
        navigate("/login");
      } else {
        toast.error(error?.data?.message || "Failed to fetch pages");
      }
    }
  }, [isError, error, navigate]);

  return (
    <div className="flex-1 p-6 bg-background min-h-screen">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
        <h2 className="text-2xl font-semibold">📄 All Pages</h2>
        <Button
          onClick={() => navigate("/admin/pages/create")}
          className="flex items-center gap-2"
          aria-label="Create new page"
        >
          <Plus size={18} />
          Create New Page
        </Button>
      </div>

      <Input
        type="text"
        placeholder="Search by Title or Slug"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="mb-6 max-w-md"
        aria-label="Search pages"
      />

      {isError ? (
        <div className="text-center text-red-500">
          {error?.data?.message || "Failed to fetch pages"}
          <br />
          <Button onClick={() => window.location.reload()} variant="link">
            Retry
          </Button>
        </div>
      ) : isLoading || isFetching ? (
        <div className="text-center flex items-center justify-center gap-2">
          <Loader2 className="w-5 h-5 animate-spin" /> Loading pages...
        </div>
      ) : sortedPages.length === 0 ? (
        <div className="text-center text-gray-500">📭 No pages found</div>
      ) : (
        <Table>
          <TableCaption>List of all created pages</TableCaption>
          <TableHeader>
            <TableRow>
              <TableHead
                onClick={() => toggleSortOrder("title")}
                className="cursor-pointer"
              >
                Title {sortBy === "title" && (sortOrder === "asc" ? "↑" : "↓")}
              </TableHead>
              <TableHead
                onClick={() => toggleSortOrder("slug")}
                className="cursor-pointer"
              >
                Slug {sortBy === "slug" && (sortOrder === "asc" ? "↑" : "↓")}
              </TableHead>
              <TableHead
                onClick={() => toggleSortOrder("status")}
                className="cursor-pointer"
              >
                Status {sortBy === "status" && (sortOrder === "asc" ? "↑" : "↓")}
              </TableHead>
              <TableHead
                onClick={() => toggleSortOrder("updatedAt")}
                className="cursor-pointer"
              >
                Updated On {sortBy === "updatedAt" && (sortOrder === "asc" ? "↑" : "↓")}
              </TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sortedPages.map((page) => (
              <TableRow
                key={page.slug}
                onClick={() => navigate(`/admin/pages/${page.slug}/edit`)}
                className="hover:bg-accent/30 cursor-pointer transition"
              >
                <TableCell className="font-medium">{page.title || "Untitled"}</TableCell>
                <TableCell className="text-sm text-muted-foreground">{page.slug || "N/A"}</TableCell>
                <TableCell>
                  <Badge
                    className={page.status === "published" ? "bg-green-500" : "bg-gray-400"}
                  >
                    {page.status === "published" ? "Published" : "Draft"}
                  </Badge>
                </TableCell>
                <TableCell>{formatDate(page.updatedAt)}</TableCell>
                <TableCell
                  className="text-right flex gap-2 justify-end"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={() => navigate(`/admin/pages/${page.slug}/edit`)}
                    title="Edit Page"
                    aria-label={`Edit page ${page.title || "Untitled"}`}
                  >
                    <Edit size={18} />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    onClick={(e) => handlePreview(e, page)}
                    title="Preview Page"
                    aria-label={`Preview page ${page.title || "Untitled"}`}
                  >
                    <Eye size={18} />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
};

export default PagesList;